import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useNetwork } from '@/contexts/NetworkContext'
import { LayoutsApi } from '@/services/sites/layoutsApi'
import { apiErrorDescription } from '@/services/core/apiError'
import {
  ProductFeedApi,
  type Merchant,
  type RetailProduct,
} from '@/services/productFeed/productFeedApi'

/** Creatives created at the same time. */
const CONCURRENCY = 3

/** The layout category the server gives layouts built to show a feed product. */
const RETAIL_CATEGORY = 'RETAIL'

export interface RetailCreationResult {
  created: number
  failures: { product: RetailProduct; message: string }[]
}

/**
 * Creates one creative per retail product on a line item. The server fills
 * each creative in from the product's feed entry, so all that is picked here
 * is the retail layout to use — chosen for the user when there is only one.
 */
export function useRetailCreativeCreation(lineItemId: string | undefined, enabled: boolean) {
  const { t } = useTranslation()
  const { scopeKey } = useNetwork()
  const queryClient = useQueryClient()
  const [pickedLayoutId, setLayoutId] = useState('')
  const [creating, setCreating] = useState(false)

  // Given the line item, the server narrows the list to what its marketplace
  // product allows, so the line item is part of the key.
  const { data: layouts = [], isLoading: layoutsLoading } = useQuery({
    queryKey: ['layouts', 'retail', scopeKey, lineItemId ?? ''],
    queryFn: async () =>
      (await LayoutsApi.getForCreatives({ lineItemId })).filter(
        (layout) => layout.detectedCategory === RETAIL_CATEGORY
      ),
    enabled,
    staleTime: 5 * 60 * 1000,
  })
  const layoutId = layouts.some((l) => l.id === pickedLayoutId)
    ? pickedLayoutId
    : (layouts[0]?.id ?? '')

  const create = async (
    targetLineItemId: string,
    products: RetailProduct[],
    merchants: Merchant[]
  ): Promise<RetailCreationResult> => {
    const merchantLinks = new Map(merchants.map((m) => [m.id, m.link]))
    const queue = [...products]
    const result: RetailCreationResult = { created: 0, failures: [] }
    setCreating(true)
    try {
      const worker = async () => {
        for (let product = queue.shift(); product; product = queue.shift()) {
          try {
            await ProductFeedApi.createCreative({
              lineItemId: targetLineItemId,
              layoutId,
              merchantLink: merchantLinks.get(product.merchantId) ?? '',
              itemId: product.itemId,
            })
            result.created++
          } catch (e) {
            result.failures.push({ product, message: apiErrorDescription(e) })
          }
        }
      }
      await Promise.all(Array.from({ length: Math.min(CONCURRENCY, products.length) }, worker))
    } finally {
      setCreating(false)
      queryClient.invalidateQueries({ queryKey: ['lineitem-creatives', targetLineItemId] })
      queryClient.invalidateQueries({ queryKey: ['lineitem-preview-assets', targetLineItemId] })
      queryClient.invalidateQueries({ queryKey: ['creatives'] })
    }
    if (result.failures.length > 0) {
      const [first] = result.failures
      toast.error(
        t('retailProducts.someFailed', { count: result.failures.length, total: products.length }),
        { description: `${first.product.title}: ${first.message}`, duration: 8000 }
      )
    } else {
      toast.success(t('common.done'), {
        description: t('retailProducts.created', { count: result.created }),
      })
    }
    return result
  }

  return { layouts, layoutsLoading, layoutId, setLayoutId, creating, create }
}
